import { isJoinMachine } from '../content/machine-defs';
import type { MaterialId } from '../content/materials';
import { RECIPES, recipeAccepts } from '../content/recipes';
import { findBaseEntryConveyor } from './base';
import { findEntityAt } from './grid';
import type { EntityRef } from './grid';
import type {
  ConveyorEntity,
  JoinMachineEntity,
  MachineEntity,
  SinkEntity,
  ObservableEntity,
} from './entities';
import { isTuple } from './packet';
import type { MaterialTuple, Matter } from './packet';
import type { Port } from './port';
import type { SimState } from './state';
import { DIRECTIONS, samePos, translate } from './types';
import type { Direction, EntityId, GridPos } from './types';

export interface MachinePorts {
  /** Every feeding side, in compass sweep order — the index a multi-input machine maps to its input ports. */
  inSides: Direction[];
  outSide: Direction | null;
  inCount: number;
  outCount: number;
}

/**
 * A neighboring machine counts as a conveyor-like feeder/receiver via its `pipeDirection` — the
 * direction of the conveyor it was slotted onto — so two machines built back-to-back with no
 * conveyor tile between them still chain. A machine with no recorded `pipeDirection` (e.g. one
 * built directly in tests without going through `place`) is treated as a dead end on that side.
 *
 * An observable has no stored direction of its own — it flows towards whichever neighbor it isn't
 * fed by — so its outlet is resolved recursively, with observable neighbors skipped on that inner
 * pass. That terminates (an observable never feeds another observable) and is what lets an operator
 * slotted directly in front of an observable read as fed, exactly like one behind a conveyor.
 */
export function machinePorts(state: SimState, position: GridPos): MachinePorts {
  return portsAt(state, position, true);
}

function neighborFlow(
  state: SimState,
  neighbor: EntityRef,
  neighborPos: GridPos,
  withObservables: boolean,
): Direction | undefined {
  if (neighbor.kind === 'conveyor') return state.conveyors[neighbor.id].direction;
  if (neighbor.kind === 'machine') return state.machines[neighbor.id].pipeDirection;
  if (neighbor.kind === 'observable' && withObservables) {
    return portsAt(state, neighborPos, false).outSide ?? undefined;
  }
  return undefined;
}

function portsAt(state: SimState, position: GridPos, withObservables: boolean): MachinePorts {
  let outSide: Direction | null = null;
  const inSides: Direction[] = [];
  let outCount = 0;

  for (const dir of DIRECTIONS) {
    const neighborPos = translate(position, dir);
    const neighbor = findEntityAt(state, neighborPos);
    if (!neighbor) continue;

    const flow = neighborFlow(state, neighbor, neighborPos, withObservables);
    if (flow === undefined) continue;

    if (samePos(translate(neighborPos, flow), position)) {
      inSides.push(dir);
    } else if (neighbor.kind !== 'observable') {
      outSide = dir;
      outCount++;
    }
  }

  return { inSides, outSide, inCount: inSides.length, outCount };
}

/**
 * Which input port the stream arriving from `fromPos` lands in: a machine's feeding sides are
 * ordered by the compass sweep, and that order *is* the port order. -1 when nothing on that side
 * feeds this machine.
 */
function inputIndexFrom(state: SimState, machine: MachineEntity, fromPos: GridPos): number {
  const { inSides } = machinePorts(state, machine.position);
  return inSides.findIndex((dir) => samePos(translate(machine.position, dir), fromPos));
}

/**
 * The port a packet arriving from `fromPos` belongs in. Single-input machines ignore the question
 * entirely; on a multi-input one this is what keeps two streams converging on the same tile
 * separated, instead of racing for whichever port happens to be free.
 */
export function machineInputPort(
  state: SimState,
  machine: MachineEntity,
  fromPos: GridPos | undefined,
): Port | undefined {
  if (machine.inputs.length <= 1) return machine.inputs[0];
  if (!fromPos) return undefined;
  const index = inputIndexFrom(state, machine, fromPos);
  return index === -1 ? undefined : machine.inputs[index];
}

/**
 * A join with only one stream wired holds its packets forever, backs the belt up behind it, and
 * keeps draining upkeep — but rejecting it at placement time would make incremental building
 * impossible, so it is surfaced as a warning instead (the renderer's stranded mark).
 */
export function hasUnwiredInput(state: SimState, machine: MachineEntity): boolean {
  return machinePorts(state, machine.position).inCount < machine.inputs.length;
}

const DEFAULT_DIRECTION: Direction = 'east';

export function feederDirection(state: SimState, pos: GridPos): Direction | null {
  let direction: Direction | null = null;
  let count = 0;

  for (const dir of DIRECTIONS) {
    const neighborPos = translate(pos, dir);
    const neighbor = findEntityAt(state, neighborPos);
    if (!neighbor || neighbor.kind !== 'conveyor') continue;

    const conveyor = state.conveyors[neighbor.id];
    if (samePos(translate(conveyor.position, conveyor.direction), pos)) {
      direction = conveyor.direction;
      count++;
    }
  }

  return count === 1 ? direction : null;
}

export function inferConveyorDirection(state: SimState, pos: GridPos): Direction {
  return feederDirection(state, pos) ?? DEFAULT_DIRECTION;
}

function nextConveyorInFlow(
  state: SimState,
  pos: GridPos,
  dir: Direction,
): ConveyorEntity | undefined {
  const targetPos = translate(pos, dir);
  const target = findEntityAt(state, targetPos);
  if (!target) return undefined;
  if (target.kind === 'conveyor') return state.conveyors[target.id];
  if (target.kind === 'machine') {
    const { outSide } = machinePorts(state, targetPos);
    return outSide ? nextConveyorInFlow(state, targetPos, outSide) : undefined;
  }
  return undefined;
}

export function conveyorLanes(state: SimState): Record<EntityId, number> {
  const byId = state.conveyors;
  const nextOf = new Map<EntityId, EntityId>();
  const feedersOf = new Map<EntityId, EntityId[]>();
  for (const conveyor of Object.values(byId)) {
    const next = nextConveyorInFlow(state, conveyor.position, conveyor.direction);
    if (!next) continue;
    nextOf.set(conveyor.id, next.id);
    const feeders = feedersOf.get(next.id);
    if (feeders) feeders.push(conveyor.id);
    else feedersOf.set(next.id, [conveyor.id]);
  }

  const lanes: Record<EntityId, number> = {};
  const visited = new Set<EntityId>();
  let nextLane = 0;

  for (const start of Object.values(byId)) {
    if (visited.has(start.id)) continue;
    const lane = nextLane++;
    const queue: EntityId[] = [start.id];
    visited.add(start.id);
    while (queue.length > 0) {
      const id = queue.shift()!;
      lanes[id] = lane;
      const forward = nextOf.get(id);
      if (forward !== undefined && !visited.has(forward)) {
        visited.add(forward);
        queue.push(forward);
      }
      for (const backward of feedersOf.get(id) ?? []) {
        if (!visited.has(backward)) {
          visited.add(backward);
          queue.push(backward);
        }
      }
    }
  }

  return lanes;
}

/** Conveyor IDs on active paths from subscribed observables — the subset of `activeEntityIds` that are conveyors. */
export function poweredConveyors(state: SimState): Set<EntityId> {
  const active = activeEntityIds(state);
  return new Set(
    Object.values(state.conveyors)
      .map((conveyor) => conveyor.id)
      .filter((id) => active.has(id)),
  );
}

/**
 * All entity IDs (observables, conveyors, machines) with a physical conveyor path back to an observable —
 * independent of subscription state, unlike {@link activeEntityIds}. Shared by the "powered glow"
 * visual (via `activeEntityIds`, subscription-gated) and by anything that needs to know what's
 * topologically connected regardless of subscription, e.g. `danglingConveyor` and the "stranded
 * entity" render check, so both agree on what "connected" means.
 */
export function reachableFromObservables(state: SimState): Set<EntityId> {
  const ids = new Set<EntityId>();
  const visited = new Set<string>();

  function follow(pos: GridPos, dir: Direction): void {
    const nextPos = translate(pos, dir);
    const target = findEntityAt(state, nextPos);
    // A Subject is never on a conveyor path — counting one as reached would make it "active", and
    // an active entity cannot be erased while subscribed (see `SimEngineService.canErase`).
    if (!target || target.kind === 'subject') return;

    const key = `${target.kind}:${target.id}`;
    if (visited.has(key)) return;
    visited.add(key);
    ids.add(target.id);

    if (target.kind === 'conveyor') {
      const conveyor = state.conveyors[target.id];
      follow(conveyor.position, conveyor.direction);
    } else if (target.kind === 'machine') {
      const machine = state.machines[target.id];
      const { outSide } = machinePorts(state, machine.position);
      if (outSide) follow(machine.position, outSide);
    }
  }

  for (const observable of Object.values(state.observables)) {
    ids.add(observable.id);
    const { outSide } = machinePorts(state, observable.position);
    if (outSide) follow(observable.position, outSide);
  }

  return ids;
}

/** All entity IDs (observables, conveyors, machines) on active paths from subscribed observables. */
export function activeEntityIds(state: SimState): Set<EntityId> {
  return state.subscribed ? reachableFromObservables(state) : new Set();
}

export function isBasePowered(state: SimState): boolean {
  const entry = findBaseEntryConveyor(state);
  return entry !== undefined && poweredConveyors(state).has(entry.id);
}

/**
 * Whether a physical conveyor path exists all the way from `observable` to the base's entry point —
 * independent of subscription state, unlike {@link isBasePowered}. Packets reach the sink through
 * a dedicated entry conveyor (see `base.ts`'s `findBaseEntryConveyor`), not by literally landing on
 * the sink's own grid cell, so this can't just check whether the chain's terminal entity is a sink.
 */
export function isObservableConnectedToBase(
  state: SimState,
  observable: ObservableEntity,
): boolean {
  const entry = findBaseEntryConveyor(state);
  if (!entry) return false;
  const { outSide } = machinePorts(state, observable.position);
  if (!outSide) return false;

  let pos = observable.position;
  let dir = outSide;
  const visited = new Set<string>();
  const maxIterations = state.grid.width * state.grid.height;
  for (let guard = 0; guard < maxIterations; guard++) {
    const nextPos = translate(pos, dir);
    const target = findEntityAt(state, nextPos);
    if (!target) return false;
    if (target.kind === 'conveyor' && target.id === entry.id) return true;

    const key = `${target.kind}:${target.id}`;
    if (visited.has(key)) return false;
    visited.add(key);

    if (target.kind === 'conveyor') {
      const conveyor = state.conveyors[target.id];
      pos = conveyor.position;
      dir = conveyor.direction;
    } else if (target.kind === 'machine') {
      const machine = state.machines[target.id];
      const ports = machinePorts(state, machine.position);
      if (!ports.outSide) return false;
      pos = machine.position;
      dir = ports.outSide;
    } else {
      return false;
    }
  }
  return false;
}

export interface ObservableChain {
  machines: MachineEntity[];
  sink: SinkEntity | null;
  /**
   * The multi-input machine this stream is folded into rather than flowing through. Only the
   * stream on the join's first input port carries the pipe onwards; the others end here, because
   * downstream of a join there is one stream, not two.
   */
  joinedAt: MachineEntity | null;
}

function followChain(
  state: SimState,
  pos: GridPos,
  dir: Direction,
  chain: ObservableChain,
  visited: Set<string>,
): SinkEntity | null {
  const nextPos = translate(pos, dir);
  const target = findEntityAt(state, nextPos);
  if (!target) return null;

  const key = `${target.kind}:${target.id}`;
  if (visited.has(key)) return null;
  visited.add(key);

  if (target.kind === 'sink') return state.sinks[target.id];
  if (target.kind === 'conveyor') {
    const conveyor = state.conveyors[target.id];
    return followChain(state, conveyor.position, conveyor.direction, chain, visited);
  }
  if (target.kind === 'machine') {
    const machine = state.machines[target.id];
    if (machine.inputs.length > 1 && inputIndexFrom(state, machine, pos) > 0) {
      chain.joinedAt = machine;
      return null;
    }
    chain.machines.push(machine);
    const { outSide } = machinePorts(state, machine.position);
    return outSide ? followChain(state, machine.position, outSide, chain, visited) : null;
  }
  return null;
}

export function traceObservableChain(
  state: SimState,
  observable: ObservableEntity,
): ObservableChain {
  const chain: ObservableChain = { machines: [], sink: null, joinedAt: null };
  const { outSide } = machinePorts(state, observable.position);
  if (outSide) {
    chain.sink = followChain(state, observable.position, outSide, chain, new Set());
  }
  return chain;
}

/**
 * The observable feeding one specific input port of a machine — the piece `traceObservableChain`
 * cannot give you, since it only ever walks downstream. Used to name a join's other stream.
 */
export function observableAtInput(
  state: SimState,
  machine: MachineEntity,
  portIndex: number,
): ObservableEntity | undefined {
  const { inSides } = machinePorts(state, machine.position);
  const dir = inSides[portIndex];
  if (!dir) return undefined;
  const neighborPos = translate(machine.position, dir);
  const neighbor = findEntityAt(state, neighborPos);
  if (!neighbor) return undefined;
  if (neighbor.kind === 'observable') return state.observables[neighbor.id];
  return findUpstreamObservable(state, neighborPos, new Set());
}

function feedsInto(
  state: SimState,
  neighborPos: GridPos,
  pos: GridPos,
  neighborKind: string,
  neighborId: EntityId,
): boolean {
  if (neighborKind === 'conveyor') {
    const conveyor = state.conveyors[neighborId];
    return samePos(translate(conveyor.position, conveyor.direction), pos);
  }
  if (neighborKind === 'observable' || neighborKind === 'machine') {
    const { outSide } = machinePorts(state, neighborPos);
    return outSide !== null && samePos(translate(neighborPos, outSide), pos);
  }
  return false;
}

/**
 * Like {@link machinePorts}'s `inSides`, but also recognizes an observable/machine neighbor whose own
 * output faces `pos` — `machinePorts` only classifies conveyor neighbors, so an exit conveyor
 * sitting directly downstream of a machine otherwise never resolves an incoming side.
 */
export function conveyorInSide(state: SimState, pos: GridPos): Direction | null {
  for (const dir of DIRECTIONS) {
    const neighborPos = translate(pos, dir);
    const neighbor = findEntityAt(state, neighborPos);
    if (neighbor && feedsInto(state, neighborPos, pos, neighbor.kind, neighbor.id)) return dir;
  }
  return null;
}

/**
 * `visited` is cloned rather than mutated on the way into a branch: a cell can be fed from more than
 * one side, and a shared set would let whichever branch is walked first mark entities the others
 * still need to see.
 */
function branchesFeeding<T>(
  state: SimState,
  pos: GridPos,
  visited: ReadonlySet<string>,
  walk: (neighbor: EntityRef, neighborPos: GridPos, visited: ReadonlySet<string>) => T,
): T[] {
  const branches: T[] = [];
  for (const dir of DIRECTIONS) {
    const neighborPos = translate(pos, dir);
    const neighbor = findEntityAt(state, neighborPos);
    if (!neighbor || !feedsInto(state, neighborPos, pos, neighbor.kind, neighbor.id)) continue;

    const key = `${neighbor.kind}:${neighbor.id}`;
    if (visited.has(key)) continue;
    branches.push(walk(neighbor, neighborPos, new Set(visited).add(key)));
  }
  return branches;
}

function findUpstreamObservable(
  state: SimState,
  pos: GridPos,
  visited: ReadonlySet<string>,
): ObservableEntity | undefined {
  const branches = branchesFeeding(state, pos, visited, (neighbor, neighborPos, seen) =>
    neighbor.kind === 'observable'
      ? state.observables[neighbor.id]
      : findUpstreamObservable(state, neighborPos, seen),
  );
  return branches.find((observable) => observable !== undefined);
}

/**
 * The first observable, in compass order, with a path down to `pos`. A branch that dead-ends is
 * skipped rather than ending the search, so a join still names the stream it does have while its
 * other side is only half built.
 */
export function traceUpstreamObservable(
  state: SimState,
  pos: GridPos,
): ObservableEntity | undefined {
  return findUpstreamObservable(state, pos, new Set());
}

/**
 * What can arrive at a point in the pipeline: loose materials, plus every tuple a join upstream
 * could pack out of its branches. Tuples are kept apart from materials because a tuple is not one —
 * only a `map` with a recipe that destructures it turns one back into something the set can hold.
 */
interface ReachablePackets {
  materials: Set<MaterialId>;
  tuples: MaterialTuple[];
}

function nothingReachable(): ReachablePackets {
  return { materials: new Set(), tuples: [] };
}

function mergeReachable(branches: ReachablePackets[]): ReachablePackets {
  return {
    materials: new Set(branches.flatMap((branch) => [...branch.materials])),
    tuples: branches.flatMap((branch) => branch.tuples),
  };
}

/** Every packet shape a branch can put on the pipe, tuples included, as `recipeAccepts` reads them. */
function arrivals(reachable: ReachablePackets): Matter[] {
  return [...reachable.materials, ...reachable.tuples];
}

/** What each side feeding `pos` can deliver, in the compass order that ports are numbered by. */
function branchArrivals(
  state: SimState,
  pos: GridPos,
  visited: ReadonlySet<string>,
): ReachablePackets[] {
  return branchesFeeding(state, pos, visited, (neighbor, neighborPos, seen) =>
    emittedBy(state, neighbor, neighborPos, seen),
  );
}

function reachableAt(
  state: SimState,
  pos: GridPos,
  visited: ReadonlySet<string>,
): ReachablePackets {
  return mergeReachable(branchArrivals(state, pos, visited));
}

function emittedBy(
  state: SimState,
  neighbor: EntityRef,
  neighborPos: GridPos,
  visited: ReadonlySet<string>,
): ReachablePackets {
  if (neighbor.kind === 'observable') {
    const yielded = state.observables[neighbor.id].yields.map((y) => y.material);
    return { materials: new Set(yielded), tuples: [] };
  }
  if (neighbor.kind === 'conveyor') return reachableAt(state, neighborPos, visited);
  if (neighbor.kind === 'machine') {
    return emittedByMachine(state, state.machines[neighbor.id], visited);
  }
  return nothingReachable();
}

function emittedByMachine(
  state: SimState,
  machine: MachineEntity,
  visited: ReadonlySet<string>,
): ReachablePackets {
  if (isJoinMachine(machine)) return packedByJoin(state, machine, visited);

  const incoming = reachableAt(state, machine.position, visited);
  if (machine.kind === 'map') {
    if (!machine.config) return nothingReachable();
    const recipe = RECIPES[machine.config.recipeId];
    const usable = arrivals(incoming).some((material) => recipeAccepts(recipe, material));
    return usable ? { materials: new Set([recipe.to]), tuples: [] } : nothingReachable();
  }
  if (machine.kind === 'filter') {
    const { config } = machine;
    if (!config) return nothingReachable();
    const passing = [...incoming.materials].filter((material) => config.allow.includes(material));
    return { materials: new Set(passing), tuples: [] };
  }
  return incoming;
}

/**
 * Every port's arrivals crossed with every other port's, since any pairing the branches can produce
 * is one the join will eventually pack. Tuples concatenate rather than nest, exactly as the handlers
 * pack them, so a chain of joins widens the tuple instead of deepening it.
 */
function packedByJoin(
  state: SimState,
  join: JoinMachineEntity,
  visited: ReadonlySet<string>,
): ReachablePackets {
  const ports = branchArrivals(state, join.position, visited);
  if (ports.length < join.inputs.length) return nothingReachable();

  const tuples = ports.reduce<MaterialTuple[]>(
    (packed, port) => {
      const halves = arrivals(port).map((material) => (isTuple(material) ? material : [material]));
      return packed.flatMap((tuple) => halves.map((half) => [...tuple, ...half]));
    },
    [[]],
  );

  return { materials: new Set(), tuples };
}

/**
 * Every material that could ever actually reach `pos` from upstream, narrowed by each machine's
 * transform along the way (a `map` collapses it to just its recipe's output, since only matching
 * packets ever pass through; a `filter` intersects it with the allow list; a `take` doesn't affect
 * material identity). Empty when nothing upstream can ever arrive.
 *
 * Seeded from `observable.yields` whatever the kind: no creation operator settles on a material
 * before it emits it, so what a stream *could* carry is always the source's whole yield list.
 *
 * Every feeding branch counts, so two belts merging contribute both their streams and a join
 * contributes the pairings of its own. A tuple is not a material and so never lands in the result
 * itself — it survives only as far as a `map` whose recipe destructures it.
 */
export function traceReachableMaterials(state: SimState, pos: GridPos): Set<MaterialId> {
  return reachableAt(state, pos, new Set()).materials;
}
